import { Suspense } from "react"
import { useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Lens } from "@hookform/lenses"
import { createCareProviderQuickListQueryVariables } from "@/modules/care-provider/utils/create-care-provider-quick-list-query-variables"
import { BetterDocSection } from "@/modules/patients/detail/better-doc/components/better-doc-section"
import { useApplicableBetterDocUsage } from "@/modules/patients/detail/better-doc/hooks/useApplicableBetterDocUsage"
import type { BetterDocCheck } from "@/modules/patients/detail/better-doc/hooks/useBetterDocRejectionConfirmation"
import { CareProviderPreselectionOnceSelectionIsUnblocked } from "@/modules/patients/detail/progress-note/point-of-care/components/care-provider-preselection-once-selection-is-unblocked"
import { CareProviderContent } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/care-provider-content"
import { DoctorVisitAvoidedContent } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/doctor-visit-avoided-content"
import { ReasonForChoosingType } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/reason-for-choosing-type"
import { SpecialtyContent } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/specialty-content"
import { TimeToTreatContent } from "@/modules/patients/detail/progress-note/point-of-care/components/fields/time-to-treat-content"
import { InsurerNetworkBanner } from "@/modules/patients/detail/progress-note/point-of-care/components/insurer-network-banner"
import { useAppointmentOrRegisteredPatientAddress } from "@/modules/patients/detail/progress-note/point-of-care/hooks/useAppointmentOrRegisteredPatientAddress"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import type { ContentProps, HasCareProvider } from "@/modules/patients/detail/progress-note/point-of-care/types"
import type { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { MedgateServiceInfo } from "@/modules/patients/services/usePatientByIdQuery"

type SpecialistContentProps = ContentProps & {
  pointOfCareFormLens: Lens<PointOfCareFormType>
  medgateServiceInfo: MedgateServiceInfo
  patientId: string
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  diagnosisCode: string
  setCareProvider: (careProvider: string | null) => void
  onBetterDocCheck: BetterDocCheck
  onSpecialtySelect: (specialtyId: string | undefined) => void
}

export const SpecialistContent = (props: SpecialistContentProps & HasCareProvider) => {
  const {
    pointOfCareFormLens,
    medgateServiceInfo,
    patientId,
    isActual,
    countryCode,
    timeToTreatData,
    episodeOfCareProgressNoteData,
    diagnosisCode,
    hasCareProvider,
    setCareProvider,
    onBetterDocCheck,
    onSpecialtySelect,
  } = props
  const specialtyLens = pointOfCareFormLens.focus("specialtyId")
  const timeToTreatLens = pointOfCareFormLens.focus("timeToTreatType")
  const specialty = useWatch(specialtyLens.interop())
  const timeToTreatType = useWatch(timeToTreatLens.interop())
  const { i18n } = useTranslation()

  const applicableBetterDocUsage = useApplicableBetterDocUsage({
    episodeOfCareProgressNoteData,
    diagnosisCode,
    effectivePointOfCare: "SPECIALIST",
    countryCode,
    setCareProvider,
  })

  const specialtyId = specialty?.specialty?.id
  const searchType = specialty ? "SPECIALIST_WITH_SPECIALTY" : "SPECIALIST_WITHOUT_SPECIALTY"
  const patientAddress = useAppointmentOrRegisteredPatientAddress({
    patientId,
    countryCode,
    episodeOfCareProgressNoteData,
  })
  const showInsurerNetwork = countryCode === "CH" && !!specialtyId
  const isCareProviderPreselectionEnabled =
    showInsurerNetwork && medgateServiceInfo.hasBetterDocConditionPackage && patientId !== ""

  return (
    <div className="col-span-2 gap-4">
      {isCareProviderPreselectionEnabled && (
        <Suspense fallback={null}>
          <CareProviderPreselectionOnceSelectionIsUnblocked
            careProviderQuickListQueryVariables={createCareProviderQuickListQueryVariables({
              patientId,
              language: i18n.language,
              searchType,
              specialtyId,
              showInsurerNetwork,
              patientAddress,
            })}
            applicableBetterDocUsage={applicableBetterDocUsage}
          />
        </Suspense>
      )}
      {countryCode === "CH" && medgateServiceInfo.hasInsuranceListReferralToSpecialist && <InsurerNetworkBanner />}
      {isActual && (
        <ReasonForChoosingType
          reasonForChoosingTypeLens={pointOfCareFormLens.focus("reasonForChoosingType")}
          containerClassName="pb-5"
        />
      )}
      {countryCode === "CH" && (
        <SpecialtyContent
          specialtyLens={specialtyLens}
          onSpecialtySelect={(selectedSpecialty) => onSpecialtySelect(selectedSpecialty?.specialty?.id)}
        />
      )}

      <BetterDocSection
        applicableBetterDocUsage={applicableBetterDocUsage}
        episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
      />
      <div className="flex flex-col gap-4 pt-4 @lg/patient-layout:flex-row">
        {timeToTreatData.length > 0 && (
          <TimeToTreatContent timeToTreatLens={timeToTreatLens} timeToTreatData={timeToTreatData} disabled />
        )}
        <div className="@lg/patient-layout:w-2/3">
          {countryCode === "CH" ? (
            <CareProviderContent
              pointOfCareFormLens={pointOfCareFormLens}
              searchType={searchType}
              specialtyId={specialtyId}
              showInsurerNetwork={showInsurerNetwork}
              patientAddress={patientAddress}
              timeToTreat={timeToTreatType ?? undefined}
              setCareProvider={setCareProvider}
              episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
              hasMPNReferral={medgateServiceInfo.referralToSpecialist === "MPN"}
              hasCareProvider={hasCareProvider}
              useSpecialty
              allowUnknownCareProvider
              applicableBetterDocUsage={applicableBetterDocUsage}
              onBetterDocCheck={(callback, event) => onBetterDocCheck(applicableBetterDocUsage, callback, event)}
            />
          ) : (
            <SpecialtyContent specialtyLens={specialtyLens} />
          )}
        </div>
      </div>
      <DoctorVisitAvoidedContent isDoctorVisitAvoidedLens={pointOfCareFormLens.focus("isDoctorVisitAvoided")} />
    </div>
  )
}
